import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addProductContactLinks,
  deleteProductContactLinks,
  getQaSpecDataCertificates,
  getQaSpecDataContact,
  getQaSpecDataLocations,
} from 'services/qaSpec';

const qaSpecContactKeys = {
  detail: (id) => ['qa-spec', Number(id), 'qa-spec-contact'],
};

const qaSpecLocationKeys = {
  detail: (id) => ['qa-spec', Number(id), 'qa-spec-location'],
};
const qaSpecCertificateKeys = {
  detail: (id) => ['qa-spec', Number(id), 'qa-spec-certificate'],
};

export const useGetQaSpecContact = ({ id, isEnabled }) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: qaSpecContactKeys.detail(id),
    queryFn: async () => {
      const response = await getQaSpecDataContact({
        ProductId: Number(id),
        PageIndex: 1,
        PageSize: 9999,
      });
      return response?.gridData;
    },
    enabled: isEnabled,
  });

  const refetchQaSpecContact = () => {
    queryClient.invalidateQueries({
      queryKey: qaSpecContactKeys.detail(id),
    });
  };

  return {
    contactList: data ?? [],
    loading: isLoading,
    refetchQaSpecContact,
  };
};

export const useGetQaSpecLocations = ({ id, isEnabled }) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: qaSpecLocationKeys.detail(id),
    queryFn: async () => {
      const response = await getQaSpecDataLocations({
        ProductId: Number(id),
        PageIndex: 1,
        PageSize: 9999,
      });
      return response?.gridData;
    },
    enabled: isEnabled,
  });

  const refetchQaSpecLocation = () => {
    queryClient.invalidateQueries({
      queryKey: qaSpecLocationKeys.detail(id),
    });
  };

  return {
    locationList: data ?? [],
    refetchQaSpecLocation,
  };
};

export const useGetQaSpecCertificates = ({ id, isEnabled }) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: qaSpecCertificateKeys.detail(id),
    queryFn: async () => {
      const response = await getQaSpecDataCertificates({
        ProductId: Number(id),
        PageIndex: 1,
        PageSize: 9999,
      });
      return response?.gridData;
    },
    enabled: isEnabled,
  });

  const refetchQaSpecCertificate = () => {
    queryClient.invalidateQueries({
      queryKey: qaSpecCertificateKeys.detail(id),
    });
  };

  return {
    certificateList: data ?? [],
    loading: isLoading,
    refetchQaSpecCertificate,
  };
};

export const InvalidateAllContactQueries = (id, userId) => {
  const queryClient = useQueryClient();

  const invalidateAllQueries = () => {
    queryClient.invalidateQueries({
      queryKey: qaSpecCertificateKeys.detail(id),
    });
    queryClient.invalidateQueries({
      queryKey: qaSpecLocationKeys.detail(id),
    });
    queryClient.invalidateQueries({
      queryKey: qaSpecContactKeys.detail(id),
    });
    queryClient.invalidateQueries(['get-qa-spec-required-fields']);
  };

  return { invalidateAllQueries };
};

export const InvalidateQASQueries = (id) => {
  const queryClient = useQueryClient();
  const invalidateQasQueries = () => {
    queryClient.invalidateQueries(['get-qa-spec-required-fields']);
  };
  return { invalidateQasQueries };
};

export const useMutationDeleteContactLinks = () => {
  const mutation = useMutation({ mutationFn: deleteProductContactLinks });

  return mutation;
};

export const useMutationAddContactLinks = () => {
  const mutation = useMutation({ mutationFn: addProductContactLinks });

  return mutation;
};
